import { useMemo, useState } from 'react';
import { BUREAU_FOLLOW_TEMPLATES, type BureauFollowDTO, type BureauFollowType } from '@pointage/shared';
import { toast } from '../../store/toast.js';
import { useBureauFollows, useCreateFollow, type NewFollow } from '../../api/bureau.js';
import { FollowRow } from './FollowRow.js';

type CreatableType = NewFollow['type'];
const CREATABLE: CreatableType[] = ['FOURNISSEUR', 'EVENEMENT', 'FACTURE', 'STUDIO', 'TACHE'];
const FILTERS: Array<[BureauFollowType | 'ALL', string]> = [
  ['ALL', 'Tous'], ['FACTURE', 'Factures'], ['FOURNISSEUR', 'Fournisseurs'], ['ACHAT', 'Achats'],
  ['EVENEMENT', 'Événements'], ['STUDIO', 'Studio'], ['TACHE', 'Tâches'],
];

export function NewFollowCard({ assigning = false }: { assigning?: boolean }) {
  const create = useCreateFollow();
  const [type, setType] = useState<CreatableType>('FOURNISSEUR');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [steps, setSteps] = useState('');
  const [error, setError] = useState<string | null>(null);
  const tpl = BUREAU_FOLLOW_TEMPLATES[type];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setError('Donne un intitulé au suivi.'); return; }
    if (tpl.dateField === 'event' && !date) { setError('Indique la date de l’événement.'); return; }
    if (type === 'FACTURE' && !date) { setError('Indique la date d’échéance.'); return; }
    const body: NewFollow = { type, title: title.trim() };
    if (tpl.dateField === 'event') body.eventDate = date;
    else if (tpl.dateField === 'due' && date) body.dueDate = date;
    if (type === 'TACHE') {
      const lines = steps.split('\n').map((l) => l.trim()).filter(Boolean);
      if (lines.length) body.steps = lines;
    }
    create.mutate(body, {
      onSuccess: () => {
        toast.success(assigning ? 'Suivi confié, la responsable est prévenue' : 'Suivi créé avec ses étapes');
        setTitle(''); setDate(''); setSteps(''); setError(null);
      },
      onError: () => toast.danger('Le suivi n’a pas pu être créé.'),
    });
  };

  return (
    <form className="card bx-new" onSubmit={submit} data-testid="bx-new-follow">
      <div className="bx-eyebrow">{assigning ? 'Confier quelque chose' : 'Nouveau suivi'}</div>
      <div className="bx-type-pick" role="radiogroup" aria-label="Type de suivi">
        {CREATABLE.map((t) => (
          <button
            type="button" key={t} role="radio" aria-checked={type === t}
            className={type === t ? 'bx-chip is-on' : 'bx-chip'}
            onClick={() => { setType(t); setError(null); }}
          >
            {BUREAU_FOLLOW_TEMPLATES[t].label}
          </button>
        ))}
      </div>
      <div className="bx-new-row">
        <label className="sr-only" htmlFor="bx-new-title">Intitulé</label>
        <input id="bx-new-title" className="input" value={title} placeholder={tpl.placeholder} onChange={(e) => { setTitle(e.target.value); setError(null); }} />
        {tpl.dateField !== 'none' && (
          <>
            <label className="sr-only" htmlFor="bx-new-date">{tpl.dateField === 'event' ? 'Date de l’événement' : 'Échéance'}</label>
            <input id="bx-new-date" type="date" className="input bx-date" value={date} onChange={(e) => setDate(e.target.value)} title={tpl.dateField === 'event' ? 'Date de l’événement' : 'Échéance'} />
          </>
        )}
        <button type="submit" className="btn btn-primary" disabled={create.isPending}>{assigning ? 'Confier →' : 'Créer'}</button>
      </div>
      {type === 'TACHE' && (
        <>
          <label className="sr-only" htmlFor="bx-new-steps">Étapes</label>
          <textarea id="bx-new-steps" className="input bx-steps-input" rows={3} value={steps} onChange={(e) => setSteps(e.target.value)} placeholder={'Étapes, une par ligne (facultatif)\nDemander 2 devis\nComparer et envoyer'} />
        </>
      )}
      <div className={error ? 'bx-hint is-error' : 'bx-hint'}>
        {error ?? `${tpl.hint}${tpl.dateField === 'event' ? ' Date de l’événement ci-dessus.' : tpl.dateField === 'due' ? ' Échéance ci-dessus.' : ''}`}
      </div>
    </form>
  );
}

export function SuivisView({ canManage }: { canManage: boolean }) {
  const [filter, setFilter] = useState<BureauFollowType | 'ALL'>('ALL');
  const [showClosed, setShowClosed] = useState(false);
  const open = useBureauFollows('open');
  const closed = useBureauFollows('closed');
  const list = useMemo(() => {
    const items: BureauFollowDTO[] = (showClosed ? closed.data?.items : open.data?.items) ?? [];
    return filter === 'ALL' ? items : items.filter((f) => f.type === filter);
  }, [filter, showClosed, open.data, closed.data]);
  const counts = useMemo(() => {
    const c = new Map<string, number>();
    for (const f of open.data?.items ?? []) c.set(f.type, (c.get(f.type) ?? 0) + 1);
    return c;
  }, [open.data]);

  return (
    <div className="bx-follows">
      <NewFollowCard />
      <div className="bx-filters">
        {FILTERS.map(([id, label]) => {
          const n = id === 'ALL' ? open.data?.items.length ?? 0 : counts.get(id) ?? 0;
          return (
            <button type="button" key={id} className={filter === id ? 'bx-filter is-on' : 'bx-filter'} onClick={() => setFilter(id)}>
              {label}{!showClosed && n > 0 ? <span className="bx-mono"> {n}</span> : null}
            </button>
          );
        })}
        <span className="bx-spacer" />
        <button type="button" className="bx-filter" onClick={() => setShowClosed((s) => !s)}>
          {showClosed ? '← Suivis en cours' : 'Terminés'}
        </button>
      </div>
      <div className="card bx-list">
        {(showClosed ? closed : open).isLoading && <div className="bx-empty-row">Chargement…</div>}
        {!(showClosed ? closed : open).isLoading && list.length === 0 && (
          <div className="bx-empty-row">{showClosed ? 'Aucun suivi terminé.' : 'Rien en cours. Crée un suivi ci-dessus quand une demande arrive.'}</div>
        )}
        {list.map((f) => <FollowRow key={f.id} f={f} canManage={canManage} />)}
      </div>
    </div>
  );
}
